'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

const DEMO_CREDENTIALS = {
  email: 'fanx@fanxietyclub.com',
  password: 'WeAreSOBack2026',
};

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGuestLoading, setIsGuestLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [copiedField, setCopiedField] = useState<'email' | 'password' | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const handleCopy = async (field: 'email' | 'password') => {
    const value = field === 'email' ? DEMO_CREDENTIALS.email : DEMO_CREDENTIALS.password;
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofillDemo = () => {
    setValue('email', DEMO_CREDENTIALS.email);
    setValue('password', DEMO_CREDENTIALS.password);
    setAuthError('');
  };

  const handleGuestMode = async () => {
    setIsGuestLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    router.push('/home-dashboard?guest=true');
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setAuthError('');
    try {
      await signIn(data.email, data.password);
      router.push('/home-dashboard');
    } catch (error: any) {
      setAuthError(error?.message || 'Wrong credentials — check your email and password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      {authError && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl fade-in" style={{ backgroundColor: '#fff5f5', border: '1px solid rgba(229,62,62,0.3)' }}>
          <span className="text-lg shrink-0">😭</span>
          <p className="text-sm font-semibold" style={{ color: '#c0392b' }}>{authError}</p>
        </div>
      )}

      <div className="space-y-1.5">
        <label htmlFor="login-email" className="block text-sm font-bold" style={{ color: '#0f0f0f' }}>
          Email address
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="you@fanxietyclub.com"
          suppressHydrationWarning
          className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all duration-150 focus:ring-2"
          style={{
            backgroundColor: '#ede8dc',
            border: `1.5px solid ${errors.email ? '#e53e3e' : '#d8ceb8'}`,
            color: '#0f0f0f',
          }}
          {...register('email', {
            required: 'Email address is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && (
          <p className="text-xs font-semibold" style={{ color: '#e53e3e' }}>{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="login-password" className="block text-sm font-bold" style={{ color: '#0f0f0f' }}>
          Password
        </label>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="Your password"
            suppressHydrationWarning
            className="w-full px-4 py-3 pr-12 rounded-xl text-sm font-medium outline-none transition-all duration-150"
            style={{
              backgroundColor: '#ede8dc',
              border: `1.5px solid ${errors.password ? '#e53e3e' : '#d8ceb8'}`,
              color: '#0f0f0f',
            }}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-black/5 transition-colors duration-150"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            suppressHydrationWarning
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" style={{ color: '#5a5040' }} />
          </button>
        </div>
        {errors.password && (
          <p className="text-xs font-semibold" style={{ color: '#e53e3e' }}>{errors.password.message}</p>
        )}
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" className="w-4 h-4 rounded" suppressHydrationWarning {...register('rememberMe')} />
          <span className="text-sm font-medium" style={{ color: '#5a5040' }}>Remember me</span>
        </label>
        <button type="button" className="text-sm font-bold transition-colors duration-150 hover:opacity-80" style={{ color: '#0f0f0f' }} suppressHydrationWarning>
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3.5 rounded-xl text-sm font-black transition-all duration-150 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ backgroundColor: '#0f0f0f', color: '#f0e840' }}
        suppressHydrationWarning
      >
        {isLoading ? (
          <>
            <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
            <span>Entering the chaos…</span>
          </>
        ) : (
          '⚽ Enter the Club'
        )}
      </button>

      {/* Guest mode button */}
      <button
        type="button"
        onClick={handleGuestMode}
        disabled={isGuestLoading}
        className="w-full py-3 rounded-xl text-sm font-bold transition-all duration-150 active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
        style={{ backgroundColor: '#fef3e2', color: '#92660a', border: '1.5px dashed #d4820a' }}
        suppressHydrationWarning
      >
        {isGuestLoading ? (
          <>
            <Icon name="ArrowPathIcon" size={15} variant="outline" className="animate-spin" />
            <span>Preparing emotional damage…</span>
          </>
        ) : (
          <>
            <span>😰</span>
            <span>Continue as Emotionally Unstable Guest</span>
          </>
        )}
      </button>

      <p className="text-xs text-center font-medium" style={{ color: '#5a5040' }}>
        Guest mode: limited features, unlimited suffering
      </p>

      <p className="text-sm text-center" style={{ color: '#5a5040' }}>
        Not a member yet?{' '}
        <button type="button" onClick={onSwitchToSignup} className="font-bold transition-colors duration-150 hover:opacity-80" style={{ color: '#0f0f0f' }} suppressHydrationWarning>
          Join the suffering
        </button>
      </p>
    </form>
  );
}